import { Box, Typography, makeStyles } from '@dxb-web-ui-framework/design-system';
import clsx from 'clsx';
import React, { useCallback, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { ProcessStatuses } from '../ApproveRejectAuthFlowPresenter';

export interface ActionAuthorizationPayrollButtonProps {
  payrollFileId: string;
  status?: string;
  callback?: Function;
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    justifyContent: 'center',
  },
  authActionText: {
    padding: `${theme.spacing(0.5)}px ${theme.spacing(2)}px`,
    color: 'white',
    '&:hover': {
      cursor: 'pointer',
      opacity: '0.8',
    },
  },
  reject: {
    padding: `${theme.spacing(0.5)}px ${theme.spacing(2.8)}px`,
    backgroundColor: theme.palette.primary.main,
  },
  approve: {
    backgroundColor: theme.palette.success.main,
  },
  warningText: {
    backgroundColor: theme.palette.warning.main,
    '&:hover': {
      cursor: 'default',
      opacity: '1',
    },
  },
  span: {
    margin: 'auto',
  },
}));

const ActionAuthorizationPayrollButtons: FC<ActionAuthorizationPayrollButtonProps> = ({
  payrollFileId,
  callback,
  status,
  children,
}) => {
  const { isExtraSmallScreen: isMobile } = useScreenDetector();

  const { t } = useTranslation();

  const onClick = useCallback(
    (e, name) => {
      if (callback) {
        callback(e, name, payrollFileId, false);
      }
    },
    [payrollFileId, callback],
  );

  const classes = useStyles();

  if (status && status === ProcessStatuses.PENDING) {
    return (
      <Box className={classes.root}>
        {children && <Box>{children}</Box>}
        <Box>
          <Typography
            paragraph
            component='span'
            color='primary'
            variant='body2'
            className={clsx(classes.authActionText, classes.warningText, classes.span)}>
            {t('authorizationListPage.ActionAuthorizationButtons.Pending')}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {children && <Box>{children}</Box>}
      <Box>
        <Typography
          onClick={(e: any) => onClick(e, 'reject')}
          paragraph
          component='span'
          variant='body2'
          className={clsx(classes.authActionText, classes.reject)}>
          {t('authorizationListPage.ActionAuthorizationButtons.Reject')}
        </Typography>
      </Box>
      <Box ml={isMobile ? 2 : 0}>
        <Typography
          onClick={(e: any) => onClick(e, 'approve')}
          paragraph
          component='span'
          variant='body2'
          className={clsx(classes.authActionText, classes.approve)}>
          {t('authorizationListPage.ActionAuthorizationButtons.Approve')}
        </Typography>
      </Box>
    </Box>
  );
};

export { ActionAuthorizationPayrollButtons };
